import * as THREE from 'three';
import {OrbitControls} from '../../contract-3d/components/OrbitControls';
import PermissionResolver from "../../../../service/security/PermissionResolver";
import {FeatureModel, FeatureTypeModel, IssuesModel} from "../../../../integration/representation/viewmodel";
import {FeatureGeometry} from "../../../../integration/representation/feature";
import {Destination} from "../../../../integration/representation/contract";
import {AttributeType} from "../../../../integration/datacatalog/datacatalog-services";

export type GeometryType = 'POINT' | 'LINE' | 'POLYGON' | 'MULTIPOINT' | 'MULTILINE' | 'MULTIPOLYGON';
export type SidePosition = 'LEFT' | 'RIGHT' | 'LEFT_AND_RIGHT' | 'MIDDLE' | 'CROSSING' | 'MIDDLE_LEFT' | 'MIDDLE_RIGHT' | 'LEFT_ACCESS' | 'RIGHT_ACCESS' | 'ROUNDABOUT_CENTRE' | 'LONGITUDINAL';
export type HeightReference = 'TOPP' | 'FOT' | 'UKJENT' | 'IKKE_REGISTRERT';
export type Lane = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | '11' | '12' | '1F' | '2F' | '1R'
    | '1K' | '2K' | '3K' | '4K' | '5K' | '6K' | '7K' | '8K' | '9K' | '10K' | '11K' | '12K' | '1K+' | '2K+' | '3K+'
    | '4K+' | '5K+' | '6K+' | '7K+' | '8K+' | '9K+' | '10K+' | '11K+' | '12K+' | '1S' | '2S' | '3S' | '4S' | '5S'
    | '6S' | '7S' | '8S' | '9S' | '10S' | '11S' | '12S' | '13S' | '14S' | '3B' | '4B' | '5B' | '6B' | '7B' | '8B'
    | '9B' | '10B' | '11B' | '12B' | '13B' | '14B' | '3BK' | '4BK' | '5BK' | '6BK' | '7BK' | '8BK' | '9BK' | '10BK'
    | '11BK' | '12BK' | '13BK' | '14BK' | '1O' | '2O' | '3O' | '4O' | '5O' | '6O' | '7O' | '8O' | '9O' | '10O' | '11O'
    | '12O' | '13O' | '14O' | '1OK' | '2OK' | '3OK' | '4OK' | '5OK' | '6OK' | '7OK' | '8OK' | '9OK' | '10OK' | '11OK'
    | '12OK' | '13OK' | '14OK' | '1H1' | '2H1' | '3H1' | '4H1' | '5H1' | '6H1' | '7H1' | '8H1' | '9H1' | '10H1'
    | '11H1' | '12H1' | '1H2' | '2H2' | '3H2' | '4H2' | '5H2' | '6H2' | '7H2' | '8H2' | '9H2' | '10H2' | '11H2'
    | '12H2' | '1V1' | '1V2' | '2V1' | '2V2';
export type AssociationScope = 'PROJECT_TO_PROJECT' | 'PROJECT_TO_NVDB' | 'NVDB_TO_PROJECT' | 'NVDB_ASSOCIATION_TO_PROJECT_CHILD' | null;
export type Operation = "CREATE" | "CORRECT" | "UPDATE" | "CLOSE";
export type PropOperation = "READ" | "WRITE" | "DELETE";
export type SpecialAttribute = "OPERASJON" | "VEGSYSTEMREFERANSE" | "SIDEPOSISJON" | "FELT" | "HØYDEREFERANSE";
export type Direction = "MED" | "MOT";

export class MessageResponse {
    constructor (
        public success: boolean,
        public message: string
    ){}
}

export class Association {
    constructor(
        public affiliation: string,
        public childFeature: string,
        public childFeatureId: string,
        public childFeatureType: string,
        public childFeatureTypeId: number,
        public distance: number,
        public id: string,
        public operation: PropOperation,
        public parentFeature: string,
        public parentFeatureId: string,
        public parentFeatureType: string,
        public parentFeatureTypeId: number,
        public proximityBasis: string,
        public typeId: number
    ){}
}


export class AggregatedErrors {
    constructor(
        public numNotabene: number,
        public numWarning: number,
        public numError: number
    ){}
}


export interface DisplayFeatureType extends Omit<FeatureTypeModel, "features"> {
    features: DisplayFeature[];
}

export enum LockState {OPEN, LOCKED}

export class RequiremendDescription {
    constructor(public requirement: Requirement, public id: number, public name: string){}
}
export enum Requirement{REQUIRED, RECOMMENDED, CONDITIONALLY_REQUIRED, OPTIONAL, LESS_IMPORTANT, NOT_IN_USE, UNDECIDED}

export const requirements = {
    REQUIRED: new RequiremendDescription(Requirement.REQUIRED, 1, 'Påkrevd, absolutt krav'),
    RECOMMENDED : new RequiremendDescription(Requirement.RECOMMENDED, 2, 'Påkrevd, ikke absolutt'),
    CONDITIONALLY_REQUIRED : new RequiremendDescription(Requirement.CONDITIONALLY_REQUIRED, 3, 'Betinget'),
    OPTIONAL : new RequiremendDescription(Requirement.OPTIONAL, 4, 'Valgfri'),
    LESS_IMPORTANT : new RequiremendDescription(Requirement.LESS_IMPORTANT, 7, 'Mindre viktig'),
    NOT_IN_USE : new RequiremendDescription(Requirement.NOT_IN_USE, 9, 'Historisk (oppdateres ikke)'),
    UNDECIDED : new RequiremendDescription(Requirement.UNDECIDED, 98, 'Ikke kravstilt')
};
export enum ValueType{NUMBER, STRING, ENUM, SPATIAL}



export class Comment {
    constructor(
        public id: string,
        public username: string,
        public comment: string,
        public error: boolean,
        public createdInstant: Date
    ){}
}

export class SubmitComment {
    constructor(public contractId: string,
                public objectId: string,
                public objectType: 'FEATURE' | 'FEATURE_TYPE' | 'PROJECT',
                public destination: Destination,
                public username: string,
                public error: boolean,
                public comment: string,
                public copyComment: boolean){}
}

export interface DisplayFeature extends FeatureModel {
    isSelected: boolean;
    roads: string;
}

export interface CopiableGeometryFeature {
    id: string;
    name: string;
}

export class NvdbFeature {
    public static heightRef: string = 'NN54';
    constructor(
        public id: string,
        public name: string,
        public alias: string,
        public typeId: number,
        public lastModified: Date,
        public isLocked: boolean,
        public attributes: Attribute[],
        public geometry: FeatureGeometry,
        public version: number,
        public startDato: string,
        public sluttDato: string,
        public srid: string,
        public associations: NvdbAssociation,
        public stedfestinger: Array<Stedfesting>,
        public missingAttributeTypes: AttributeType[]
    ) {}
}

export class Stedfesting {
    constructor(
        public veglenkeid: number,
        public  fra_posisjon: number,
        public til_posisjon: number,
        public kortform: string,
        public retning: string,
        public sideposisjon: string,
    ){}
}

export class CommonAssociationFeature {
    public isExporting?: boolean;
    public isDirty?: boolean;
    constructor(
        public id: string,
        public name: string,
        public alias: string,
        public descriptiveName: string,
        public typeId: number,
        public attributes: Attribute[],
        public geometry: FeatureGeometry,
        public version: number,
        public heightRef: string,
        public srid: number,
        public associations: NvdbAssociation,
        public missingAttributeTypes: AttributeType[],
        public operation?: string
    ){}
}

export class NvdbAssociation {
    constructor(
        public foreldre: Array<NvdbRelative>,
        public barn: Array<NvdbRelative>
    ){}
}

export class NvdbRelative {
    constructor(
        public type: {
            id: number;
            navn: string;
        },
        public vegobjekter: Array<{
            id: string;
        }>
    ){}
}

export class CommonFeature {
    constructor(
        public id: string,
        public name: string,
        public alias: string,
        public typeId: number,
        public attributes: Attribute[],
        public geometry: FeatureGeometry
    ){}
}

export class ColHeader {
    constructor(
        public key: string,
        public name: string,
        public popupTitle: string,
        public popupDescription: string
    ){}
}

export class Measurement {
    constructor(
        public measurementMethod: string,
        public measurementAccuracy: string
    ){}
}
export class Attribute {
    constructor(
        public name: string,
        public id: string,
        public value: string,
        public typeId: number,
        public issues: IssuesModel,
        public operation: string
    ){}
}

class Permission {
    constructor(
        public resources: string[],
        public actions: string[],
        public contractId: string,
        public contractGroupId: string
    ){}

    permits(requiredPermission: Permission): boolean {
        return Permission.matches(this.resources, requiredPermission.resources) &&
            Permission.matches(this.actions, requiredPermission.actions) &&
            (
                !!this.contractId && this.contractId === requiredPermission.contractId || // Permitted for specific contract
                !!this.contractGroupId && this.contractGroupId === requiredPermission.contractGroupId || // Permitted for specific contract group
                (!requiredPermission.contractGroupId && !requiredPermission.contractId) // Neither contract nor contract group required = global permit
            );
    }

    static matches(granted: string[], required: string[]): boolean {
        return granted.includes('*') || required.every(req => granted.includes(req));
    }

    static fromDescriptor(permissionDesc: string, contractId?: string, contractGroupId?: string): Permission {
        const permissionItems = permissionDesc.split(':');
        return new Permission([permissionItems[0].toUpperCase()], [permissionItems[1].toUpperCase()], contractId, contractGroupId);
    }
}

export class UserSession {
    private permissionResolver: PermissionResolver;

    constructor(readonly permissions: any[],
                readonly username: string,
                readonly name: string,
                readonly lastLogin: Date,
                readonly featureToggles: string[],
                readonly globalAdmin: boolean,
                readonly groupAdminGroupIds: string[],
                readonly changelogStatus: string,
                readonly sensitiveTypes: string[]) {
        this.permissionResolver = new PermissionResolver(
            permissions.map(p => new Permission(p.resources, p.actions, p.contractId, p.contractGroupId)));
        this.username = username;
        this.name = name;
        this.lastLogin = lastLogin;
        this.featureToggles = featureToggles;
        this.globalAdmin = globalAdmin;
        this.groupAdminGroupIds = groupAdminGroupIds ?? [];
        this.changelogStatus = changelogStatus;
    }

    hasPermission(requiredPermission: string, contractId?: string, contractGroupId?: string): boolean {
        return this.permissionResolver.hasPermission(requiredPermission, contractId, contractGroupId);
    }

    hasPermissionInAnyScope(resource: string, action: string){
        return this.permissionResolver.hasPermissionInAnyScope(resource, action);
    }

    isGroupAdmin(contractGroupId: string): boolean {
        return this.groupAdminGroupIds.includes(contractGroupId);
    }

    featureEnabled(feature: string): boolean {
        return this.featureToggles.includes(feature);
    }
    
    withFeatureToggles(toggles: string[]): UserSession {
        return new UserSession(this.permissions, this.username, this.name, this.lastLogin,
            toggles, this.globalAdmin, this.groupAdminGroupIds, this.changelogStatus, this.sensitiveTypes);
    }
}

export class FeatureAttributeChange {
    constructor(
        public objectId: string,
        public oldValue: string,
        public newValue: string,
        public attributeTypeId: number,
        public featureTypeId: number
    ){}
}

export class FeatureAttributeCopy {
    constructor(
        public featureTypeId: number,
        public featureId: string,
        public attributeTypeId: number,
        public value: string,
        public onlyCopyToSelection: boolean
    ){}
}

export class BoundingBox {
    constructor(
        public northEast: {lat: number, lng: number},
        public southWest: {lat: number, lng: number},
        public srid: string
    ){}

    asString() {
        return [this.southWest.lng, this.southWest.lat, this.northEast.lng, this.northEast.lat].join(',');
    }
}

export class NvdbDeletion {
    constructor(
        public id: string,
        public nvdbSubmissionId: string,
        public nvdbFeature: NvdbFeature
    ){}
}

export class SceneData {
    constructor(
        public scene: THREE.Scene,
        public renderer: THREE.WebGLRenderer,
        public camera: THREE.PerspectiveCamera,
        public controls: OrbitControls,
        public axisHelper: { scene: THREE.Scene, renderer: THREE.WebGLRenderer, camera: THREE.PerspectiveCamera }
    ){}
}

export class Ground3dData {
    constructor(
        public show: boolean,
        public follow: boolean,
        public filled: boolean,
        public squareSize: number,
        public height: number
    ){}
}

export class NvdbIdRef {
    constructor(
        public id: string,
        public version: number,
    ){}
}

export class SidePositionEdit{
    constructor(
        public sidePosition: SidePosition,
        public featureId: string,
        public linkId: number,
    ){}
}

export class LaneEdit{
    constructor(
        public lane: string,
        public featureId: string,
        public linkId: number,
    ) {}
}

export type FeltKode = {
    value: Lane,
    name: string,
};


